if (!customElements.get('preorder-alert')) {
  customElements.define(
    'preorder-alert',
    class PreorderAlert extends HTMLElement {
      constructor() {
        super();
        
        this.onOverlayClick = this.onOverlayClick.bind(this);
        this.onGoBackClick = this.onGoBackClick.bind(this);
        this.onContinueClick = this.onContinueClick.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);
      }

      connectedCallback() {
        this.overlay = this.querySelector('.overlay');
        this.goBackButton = this.querySelector('[data-action="go-back"]');
        this.continueButton = this.querySelector('[data-action="continue"]');
        
        if (this.overlay) {
          this.overlay.addEventListener('click', this.onOverlayClick);
        }
        
        if (this.goBackButton) {
          this.goBackButton.addEventListener('click', this.onGoBackClick);
        }
        
        if (this.continueButton) {
          this.continueButton.addEventListener('click', this.onContinueClick);
        }
        
        document.addEventListener('keydown', this.onKeyDown);
      }

      disconnectedCallback() {
        if (this.overlay) {
          this.overlay.removeEventListener('click', this.onOverlayClick);
        }
        
        if (this.goBackButton) {
          this.goBackButton.removeEventListener('click', this.onGoBackClick);
        }
        
        if (this.continueButton) {
          this.continueButton.removeEventListener('click', this.onContinueClick);
        }
        
        document.removeEventListener('keydown', this.onKeyDown);
      }

      onOverlayClick(event) {
        if (event.target === this.overlay) {
          this.hide();
        }
      }

      onGoBackClick(event) {
        event.preventDefault();
        this.hide();
      }

      onContinueClick(event) {
        event.preventDefault();
        
        // Proceed with original checkout action
        if (this.originalCheckoutAction) {
          this.originalCheckoutAction();
        } else {
          console.error('No original checkout action found');
        }
        
        this.hide();
      }

      onKeyDown(event) {
        if (event.key === 'Escape' && this.isVisible()) {
          this.hide();
        }
      }

      show(checkoutAction = null) {
        // Don't show if already visible to prevent multiple calls
        if (this.isVisible()) {
          return;
        }
        
        this.originalCheckoutAction = checkoutAction;
        this.setAttribute('open', '');
        this.removeAttribute('hidden');
        
        // Trap focus
        if (this.goBackButton) {
          this.goBackButton.focus();
        }
        
        // Add body class to prevent scrolling
        document.body.classList.add('preorder-alert-open');
      }

      hide() {
        this.removeAttribute('open');
        this.setAttribute('hidden', '');
        document.body.classList.remove('preorder-alert-open');
        this.originalCheckoutAction = null;
        
        // Reset checkout progress flag when hiding
        if (window.preorderManager) {
          window.preorderManager.checkoutInProgress = false;
        }
      }

      isVisible() {
        return this.hasAttribute('open');
      }
    }
  );
}

// Preorder detection and checkout interception
class PreorderCheckoutManager {
  constructor() {
    this.temporarilyDisableInterception = false;
    this.checkoutInProgress = false;
    this.init();
  }

  init() {
    // Immediately start intercepting
    this.interceptCheckoutNavigation();
    
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.attachListeners());
    } else {
      this.attachListeners();
    }
  }

  attachListeners() {
    // Intercept form submissions to checkout with high priority
    document.addEventListener('submit', (event) => {
      if (this.temporarilyDisableInterception) return;
      
      const form = event.target;
      const isCheckoutForm = this.isCheckoutForm(form);
      
      if (isCheckoutForm) {
        this.handleCheckoutSubmit(event, form);
      }
    }, true); // Use capture phase for higher priority

    // Intercept checkout button clicks with high priority
    document.addEventListener('click', (event) => {
      if (this.temporarilyDisableInterception) return;
      
      const checkoutButton = event.target.closest('button[name="checkout"]');
      if (checkoutButton) {
        this.handleCheckoutClick(event, checkoutButton);
      }
    }, true); // Use capture phase for higher priority

    // Additional safety: intercept any navigation to checkout
    this.interceptCheckoutNavigation();
  }

  isCheckoutForm(form) {
    // Check if this is a cart-related form that can trigger checkout
    const hasCheckoutButton = form.querySelector('button[name="checkout"]');
    const isCartForm = form.id === 'cart';
    const actionIsCart = form.action && form.action.includes('/cart');
    const hasCartFormElements = form.querySelector('[form="cart"]');
    

    
    return hasCheckoutButton || isCartForm || (actionIsCart && hasCheckoutButton);
  }

  async handleCheckoutSubmit(event, form) {
    
    // Always prevent first, then check if we should allow
    event.preventDefault();
    event.stopImmediatePropagation();
    
    try {
      // Get current cart data
      const cartData = await this.getCartData();
      
      const hasMixed = this.hasMixedPreorderItems(cartData);
      
      if (hasMixed) {
        
        // Show preorder alert
        const alert = document.getElementById('PreorderAlert');
        if (alert) {
          alert.show(() => {
            // Continue with original checkout
            this.proceedWithFormSubmit(form);
          });
        } else {
          console.error('PreorderAlert element not found');
          // Fallback: allow checkout if alert not found
          this.proceedWithFormSubmit(form);
        }
      } else {
        // No mixed items, allow checkout to proceed
        this.proceedWithFormSubmit(form);
      }
    } catch (error) {
      console.error('Error checking preorder items:', error);
      // Allow checkout to proceed on error
      this.proceedWithFormSubmit(form);
    }
  }

  async handleCheckoutClick(event, button) {
    
    // Always prevent first
    event.preventDefault();
    event.stopImmediatePropagation();
    
    // Prevent multiple simultaneous checkout attempts
    if (this.checkoutInProgress) {
      return;
    }
    
    this.checkoutInProgress = true;
    
    try {
      // Get current cart data
      const cartData = await this.getCartData();
      
      if (this.hasMixedPreorderItems(cartData)) {
        
        // Show preorder alert
        const alert = document.getElementById('PreorderAlert');
        if (alert) {
          alert.show(() => {
            // Continue with original checkout
            this.proceedWithCheckout(button);
          });
        } else {
          console.error('PreorderAlert element not found');
          this.proceedWithCheckout(button);
        }
      } else {
        this.proceedWithCheckout(button);
      }
    } catch (error) {
      console.error('Error checking preorder items:', error);
      // Allow checkout to proceed on error
      this.proceedWithCheckout(button);
    } finally {
      // Reset flag after a delay
      setTimeout(() => {
        this.checkoutInProgress = false;
      }, 1000);
    }
  }

  interceptCheckoutNavigation() {
    // Intercept any programmatic navigation to checkout
    const manager = this;
    const originalSubmit = HTMLFormElement.prototype.submit;
    
    HTMLFormElement.prototype.submit = function() {
      const form = this;
      
      if (manager && !manager.temporarilyDisableInterception) {
        const hasCheckoutButton = form.querySelector('button[name="checkout"]');
        const isCartForm = form.id === 'cart';
        const actionIsCart = form.action && form.action.includes('/cart');
        
        if (hasCheckoutButton || isCartForm || actionIsCart) {
          
          // Create a fake event object
          const fakeEvent = { 
            preventDefault: () => {}, 
            stopImmediatePropagation: () => {},
            target: form
          };
          
          manager.handleCheckoutSubmit(fakeEvent, form);
          return;
        }
      }
      
      originalSubmit.call(this);
    };
  }

  async getCartData() {
    try {
      const response = await fetch('/cart.json');
      return await response.json();
    } catch (error) {
      console.error('Error fetching cart data:', error);
      throw error;
    }
  }

  hasMixedPreorderItems(cartData) {
    if (!cartData.items || cartData.items.length === 0) {
      return false;
    }

    let hasPreorderItems = false;
    let hasInStockItems = false;
    let preorderItems = [];
    let inStockItems = [];

    cartData.items.forEach(item => {
      const isPreorder = this.isPreorderItem(item);
      
      if (isPreorder) {
        hasPreorderItems = true;
        preorderItems.push(item.title || item.product_title);
      } else {
        hasInStockItems = true;
        inStockItems.push(item.title || item.product_title);
      }
    });

    return hasPreorderItems && hasInStockItems;
  }

  isPreorderItem(item) {
    // Check if title contains "preorder" (case insensitive)
    const title = (item.title || item.product_title || '').toLowerCase();
    return title.includes('preorder');
  }

  proceedWithFormSubmit(form) {
    // Temporarily remove our event listener to avoid infinite loop
    this.temporarilyDisableInterception = true;
    
    
    // Try direct navigation to checkout first
    try {
      window.location.href = '/checkout';
      return;
    } catch (error) {
    }
    
    // Fallback: Create a new form element to avoid our interceptors
    const newForm = document.createElement('form');
    
    // Always use POST method for checkout
    newForm.method = 'POST';
    newForm.style.display = 'none';
    
    // Determine the correct action URL
    if (form.action && form.action.includes('/cart')) {
      // For cart forms, use the same action but ensure checkout behavior
      newForm.action = form.action;
    } else {
      // Fallback to cart URL
      newForm.action = '/cart';
    }
    
    // Copy all form data
    const formData = new FormData(form);
    for (let [key, value] of formData.entries()) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = key;
      input.value = value;
      newForm.appendChild(input);
    }
    
    // Add checkout button data - this is crucial for Shopify to redirect to checkout
    const checkoutInput = document.createElement('input');
    checkoutInput.type = 'hidden';
    checkoutInput.name = 'checkout';
    checkoutInput.value = 'Check out';
    newForm.appendChild(checkoutInput);
    
    // Submit the new form
    document.body.appendChild(newForm);
    newForm.submit();
    
    // Clean up
    setTimeout(() => {
      this.temporarilyDisableInterception = false;
      if (newForm.parentNode) {
        newForm.parentNode.removeChild(newForm);
      }
    }, 100);
  }

  proceedWithCheckout(button) {
    // Temporarily remove our event listener to avoid infinite loop
    this.temporarilyDisableInterception = true;
    
    
    // If button has a form, submit it properly
    if (button.form) {
      this.proceedWithFormSubmit(button.form);
    } else {
      // Fallback: try to click the button
      setTimeout(() => {
        button.click();
        this.temporarilyDisableInterception = false;
      }, 100);
    }
  }
}

// Initialize the manager
window.preorderManager = new PreorderCheckoutManager();
